import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { cn } from "@punto-firma/ui/lib/utils";

/** Pone `<title>` mientras la pantalla está montada. */
export const useDocumentTitle = (title?: string) => {
  const { t } = useTranslation("common");
  const appName = t("appName");
  useEffect(() => {
    document.title =
      title && title !== appName ? `${title} · ${appName}` : appName;
  }, [title, appName]);
};

/**
 * Contenedor de una pantalla: título (Orbikular), descripción opcional,
 * acciones a la derecha y, debajo, pestañas (`TabNav`) si las hay.
 */
export const Page = ({
  title,
  description,
  actions,
  tabs,
  children,
  className,
  parent,
  badge,
}: {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  tabs?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  /** Miga de pan: la sección de la que cuelga («Certificados /»). */
  parent?: { to: string; label: string };
  /** Junto al título (estado). */
  badge?: React.ReactNode;
}) => {
  useDocumentTitle(title);

  return (
    <div
      className={cn("mx-auto w-full max-w-6xl space-y-6 p-4 md:p-8", className)}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            {parent ? (
              <Link
                to={parent.to}
                className="text-sm text-muted-foreground underline underline-offset-4 hover:no-underline"
              >
                {parent.label}
              </Link>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="break-words font-display text-2xl font-semibold md:text-3xl">
                {title}
              </h1>
              {badge}
            </div>
            {description ? (
              <p className="max-w-prose text-sm text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          {actions ? (
            <div className="flex flex-wrap items-center gap-2">{actions}</div>
          ) : null}
        </div>
        {tabs}
      </div>
      {children}
    </div>
  );
};
